import React, { useState, useEffect } from 'react';
import { subscribeToCollection, saveDocument, deleteDocument, removeFieldFromDocument } from '../firebase';
import { getUserName, isSameUser } from '../utils/userUtils';
import LoadingChip from './LoadingChip';
import { Vote } from 'lucide-react';

const MIN_OPTIONS = 2;

export default function PollBoard({ users = [] }) {
  const [polls, setPolls] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [showAddForm, setShowAddForm] = useState(false);
  const [newQuestion, setNewQuestion] = useState('');
  const [newOptions, setNewOptions] = useState(['', '']);

  const [editingPollId, setEditingPollId] = useState(null);
  const [editQuestion, setEditQuestion] = useState('');
  const [editOptions, setEditOptions] = useState(['', '']);

  const userName = localStorage.getItem('dayones_user_name') || '';

  useEffect(() => {
    const unsubscribe = subscribeToCollection('polls', (data) => {
      const sorted = [...data].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
      setPolls(sorted);
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // ── Add Poll form helpers ───────────────────────────────────────────────
  const handleNewOptionChange = (idx, val) => {
    setNewOptions(prev => prev.map((o, i) => (i === idx ? val : o)));
  };
  const handleAddNewOptionRow = () => setNewOptions(prev => [...prev, '']);
  const handleRemoveNewOptionRow = (idx) => {
    if (newOptions.length > MIN_OPTIONS) {
      setNewOptions(prev => prev.filter((_, i) => i !== idx));
    }
  };

  const handleCreatePoll = async (e) => {
    e.preventDefault();
    const question = newQuestion.trim();
    const cleanOptions = newOptions.map(o => o.trim()).filter(Boolean);
    if (!question || cleanOptions.length < MIN_OPTIONS) {
      alert(`Please enter a question and at least ${MIN_OPTIONS} options.`);
      return;
    }
    const ts = Date.now();
    const id = `poll_${ts}`;
    const creator = userName || (users[0]?.id || 'Guest');
    const poll = {
      id,
      question,
      options: cleanOptions.map((text, i) => ({ id: `opt_${ts}_${i}`, text })),
      votes: {},
      createdBy: creator,
      createdAt: ts
    };
    await saveDocument('polls', id, poll);
    setShowAddForm(false);
    setNewQuestion('');
    setNewOptions(['', '']);
  };

  // ── Edit Poll form helpers ──────────────────────────────────────────────
  const handleStartEditPoll = (poll) => {
    setEditingPollId(poll.id);
    setEditQuestion(poll.question);
    setEditOptions((poll.options || []).map(o => o.text));
  };

  const handleEditOptionChange = (idx, val) => {
    setEditOptions(prev => prev.map((o, i) => (i === idx ? val : o)));
  };
  const handleAddEditOptionRow = () => setEditOptions(prev => [...prev, '']);
  const handleRemoveEditOptionRow = (idx) => {
    if (editOptions.length > MIN_OPTIONS) {
      setEditOptions(prev => prev.filter((_, i) => i !== idx));
    }
  };

  const handleCancelEditPoll = () => setEditingPollId(null);

  const handleSaveEditPoll = async (poll) => {
    const question = editQuestion.trim();
    const cleanOptions = editOptions.map(o => o.trim()).filter(Boolean);
    if (!question || cleanOptions.length < MIN_OPTIONS) {
      alert(`Please enter a question and at least ${MIN_OPTIONS} options.`);
      return;
    }

    const ts = Date.now();
    const updated = {
      ...poll,
      question,
      options: cleanOptions.map((text, i) => ({ id: `opt_${ts}_${i}`, text })),
      votes: {}
    };
    await saveDocument('polls', poll.id, updated);
    setEditingPollId(null);
  };

  const handleDeletePoll = async (poll) => {
    if (!window.confirm(`Delete the poll "${poll.question}"? This cannot be undone.`)) return;
    await deleteDocument('polls', poll.id);
    if (editingPollId === poll.id) setEditingPollId(null);
  };

  // ── Voting ───────────────────────────────────────────────────────────────
  const handleVoteOption = async (poll, optionId) => {
    if (!userName) {
      alert('Please select your name / log in to vote!');
      return;
    }
    const currentVotes = poll.votes || {};
    const currentChoice = currentVotes[userName];
    if (currentChoice === optionId) {
      // Clicking your current choice again removes your vote. This has to
      // be a real field delete (not a saveDocument merge-write with a
      // smaller `votes` object) — Firestore's merge writes only add/update
      // keys present in the payload, they never remove keys that are
      // already stored, so the vote would silently come right back.
      await removeFieldFromDocument('polls', poll.id, `votes.${userName}`);
    } else {
      const newVotes = { ...currentVotes, [userName]: optionId };
      await saveDocument('polls', poll.id, { ...poll, votes: newVotes });
    }
  };

  return (
    <div className="poll-board" style={{ marginBottom: '32px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px', flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span className="poll-board-icon-badge">
            <Vote size={18} strokeWidth={2} />
          </span>
          <div>
            <h3 style={{ fontFamily: 'var(--font-title)', fontSize: '1.25rem', margin: 0 }}>
              Polls
            </h3>
          </div>
        </div>
        <button
          className={showAddForm ? 'btn-secondary' : 'btn-primary'}
          style={{ fontSize: '0.85rem', padding: '8px 18px', borderRadius: '0px' }}
          onClick={() => { setShowAddForm(v => !v); setEditingPollId(null); }}
        >
          {showAddForm ? '✕ Cancel' : '+ New Poll'}
        </button>
      </div>

      {showAddForm && (
        <form
          onSubmit={handleCreatePoll}
          className="poll-edit-card"
          style={{ marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}
        >
          <div className="form-group">
            <label>Question</label>
            <input
              type="text"
              className="input-field"
              placeholder="e.g. Where should we eat Friday night?"
              value={newQuestion}
              onChange={e => setNewQuestion(e.target.value)}
              required
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{ fontSize: '0.8rem', fontWeight: '600', textTransform: 'uppercase', color: 'var(--color-text-muted)', letterSpacing: '0.5px' }}>
              Options
            </label>
            {newOptions.map((opt, idx) => (
              <div key={idx} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="text"
                  className="input-field"
                  placeholder={`Option ${idx + 1}`}
                  value={opt}
                  onChange={e => handleNewOptionChange(idx, e.target.value)}
                  style={{ flex: 1, fontSize: '0.9rem' }}
                />
                {newOptions.length > MIN_OPTIONS && (
                  <button
                    type="button"
                    className="btn-inline-delete-cross"
                    style={{ fontSize: '1rem' }}
                    onClick={() => handleRemoveNewOptionRow(idx)}
                    title="Remove option"
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              className="btn-secondary"
              style={{ alignSelf: 'flex-start', padding: '5px 14px', fontSize: '0.8rem', borderRadius: '0px' }}
              onClick={handleAddNewOptionRow}
            >
              + Add Option
            </button>
          </div>

          <button type="submit" className="btn-primary" style={{ alignSelf: 'flex-start', fontSize: '0.85rem', padding: '8px 20px' }}>
            Create Poll
          </button>
        </form>
      )}

      {isLoading ? (
        <LoadingChip text="Loading polls..." />
      ) : polls.length === 0 ? (
        <div className="glass-panel" style={{ padding: '40px', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: '0.85rem', fontStyle: 'italic' }}>
          No polls yet. Ask the group something above!
        </div>
      ) : (
        <div className="poll-grid">
          {polls.map(poll => {
            const isEditingThis = editingPollId === poll.id;
            const pollVotes = poll.votes || {};
            const totalVotes = Object.keys(pollVotes).length;
            const myChoice = userName ? pollVotes[userName] : null;
            const isCreator = poll.createdBy && userName && isSameUser(users, poll.createdBy, userName);
            const existingVoteCount = totalVotes;

            if (isEditingThis) {
              return (
                <div
                  key={poll.id}
                  className="poll-edit-card"
                  style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}
                >
                  <h4 style={{ margin: 0, fontSize: '1rem', color: 'var(--color-text-main)' }}>Edit Poll</h4>

                  {existingVoteCount > 0 && (
                    <div
                      style={{
                        fontSize: '0.78rem',
                        fontWeight: '600',
                        color: 'var(--color-danger, #c0392b)',
                        background: 'rgba(192, 57, 43, 0.08)',
                        border: '1px solid rgba(192, 57, 43, 0.25)',
                        padding: '8px 12px'
                      }}
                    >
                      ⚠️ Saving changes will reset all {existingVoteCount} current vote{existingVoteCount === 1 ? '' : 's'}.
                    </div>
                  )}

                  <div className="form-group">
                    <label>Question</label>
                    <input
                      type="text"
                      className="input-field"
                      value={editQuestion}
                      onChange={e => setEditQuestion(e.target.value)}
                    />
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <label style={{ fontSize: '0.8rem', fontWeight: '600', textTransform: 'uppercase', color: 'var(--color-text-muted)', letterSpacing: '0.5px' }}>
                      Options
                    </label>
                    {editOptions.map((opt, idx) => (
                      <div key={idx} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <input
                          type="text"
                          className="input-field"
                          placeholder={`Option ${idx + 1}`}
                          value={opt}
                          onChange={e => handleEditOptionChange(idx, e.target.value)}
                          style={{ flex: 1, fontSize: '0.9rem' }}
                        />
                        {editOptions.length > MIN_OPTIONS && (
                          <button
                            type="button"
                            className="btn-inline-delete-cross"
                            style={{ fontSize: '1rem' }}
                            onClick={() => handleRemoveEditOptionRow(idx)}
                            title="Remove option"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      className="btn-secondary"
                      style={{ alignSelf: 'flex-start', padding: '5px 14px', fontSize: '0.8rem', borderRadius: '0px' }}
                      onClick={handleAddEditOptionRow}
                    >
                      + Add Option
                    </button>
                  </div>

                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      className="btn-primary"
                      style={{ fontSize: '0.8rem', padding: '7px 20px', background: 'var(--color-accent)' }}
                      onClick={() => handleSaveEditPoll(poll)}
                    >
                      {existingVoteCount > 0 ? '✓ Save and Reset Votes' : '✓ Save Changes'}
                    </button>
                    <button className="btn-secondary" style={{ fontSize: '0.8rem', padding: '7px 14px' }} onClick={handleCancelEditPoll}>
                      Cancel
                    </button>
                  </div>
                </div>
              );
            }

            return (
              <div key={poll.id} className="poll-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px', marginBottom: '12px' }}>
                  <div>
                    <h4 style={{ margin: 0, fontSize: '1.05rem', color: 'var(--color-text-main)', fontFamily: 'var(--font-title)' }}>
                      {poll.question}
                    </h4>
                    {poll.createdBy && (
                      <span className="activity-tag" style={{ display: 'inline-block', marginTop: '6px' }}>
                        Asked by {getUserName(users, poll.createdBy)}
                      </span>
                    )}
                  </div>
                  {isCreator && (
                    <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                      <button className="btn-inline-edit" onClick={() => handleStartEditPoll(poll)} title="Edit poll">
                        [Edit]
                      </button>
                      <button className="btn-inline-delete" onClick={() => handleDeletePoll(poll)} title="Delete poll">
                        [Delete]
                      </button>
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {(poll.options || []).map(opt => {
                    const optVoters = Object.entries(pollVotes)
                      .filter(([, choice]) => choice === opt.id)
                      .map(([voterId]) => voterId);
                    const optVoteCount = optVoters.length;
                    const pct = totalVotes > 0 ? Math.round((optVoteCount / totalVotes) * 100) : 0;
                    const isMine = myChoice === opt.id;

                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => handleVoteOption(poll, opt.id)}
                        className={`poll-option-row${isMine ? ' is-mine' : ''}`}
                        title={isMine ? 'Click again to remove your vote' : 'Vote for this option'}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
                          <span style={{ fontSize: '0.88rem', fontWeight: isMine ? '700' : '500', color: isMine ? 'var(--color-primary)' : 'var(--color-text-main)', display: 'flex', alignItems: 'center' }}>
                            <span className={`poll-option-box${isMine ? ' is-mine' : ''}`} />
                            {opt.text}
                          </span>
                          <span style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                            {optVoteCount} vote{optVoteCount === 1 ? '' : 's'} · {pct}%
                          </span>
                        </div>
                        <div className="poll-bar-track">
                          <div className="poll-bar-fill" style={{ width: `${pct}%` }} />
                        </div>
                        {optVoteCount > 0 && (
                          <div style={{ fontSize: '0.68rem', color: 'var(--color-text-muted)', marginTop: '4px' }}>
                            {optVoters.map(v => getUserName(users, v)).join(', ')}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>

                <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <span className="activity-tag">
                    {totalVotes} total vote{totalVotes === 1 ? '' : 's'}
                  </span>
                  {myChoice && (
                    <span style={{ fontSize: '0.68rem', color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
                      Click your choice again to remove your vote
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
